import type { ReactNode } from 'react'
import { useLocation } from 'react-router'
import type { InterpolationFunction } from 'styled-components'

import { SideNavigation, TopNavigation, useSideNavigation } from '@loadsmart/loadsmart-ui'
import { Layout, Text } from '@loadsmart/miranda-react'

import user from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'
import { PORTAL_LINK_SHIPMENTS_PATH } from 'rate-command-center/domain/PortalLinkRoute'
import { PORTAL_BASE_PATH } from 'shipperguide/core/domain/PortalRoute'

import shipperGuideDarkLogo from '../../assets/shipperguide-logo-dark.svg'
import shipperGuideDefaultLogo from '../../assets/shipperguide-logo.svg'
import {
  StyledMainWrapper,
  StyledPageWIthSideNav,
  StyledSideNavigationWrapper,
  StyledTopNavigationWrapper,
} from './styles'

interface LoggedInNavigationProps {
  readonly children: ReactNode
  readonly color?: string
  readonly padding?: string | InterpolationFunction<any>
}

function SideNavCollapseWrapper({
  open,
  children,
}: {
  readonly open: boolean
  readonly children: ReactNode
}) {
  return <StyledSideNavigationWrapper $open={open}>{children}</StyledSideNavigationWrapper>
}

function TopNavCollapseWrapper({ children }: { readonly children: ReactNode }) {
  return <StyledTopNavigationWrapper>{children}</StyledTopNavigationWrapper>
}

function NavigationLogo({ navPosition }: { readonly navPosition: 'side' | 'top' }) {
  return (
    <img
      src={navPosition === 'side' ? shipperGuideDefaultLogo : shipperGuideDarkLogo}
      width={150}
      alt="ShipperGuide"
    />
  )
}

function isActivePath(path: string, pathname: string) {
  return pathname.startsWith(path)
}

function LoggedInNavigation({ children, color, padding }: LoggedInNavigationProps) {
  const { open, hide, toggle } = useSideNavigation()
  const { enableShipperListPage } = useConfigContext()
  const { pathname } = useLocation()

  return (
    <StyledPageWIthSideNav>
      <SideNavCollapseWrapper open={open}>
        <SideNavigation>
          <SideNavigation.CloseButton size={24} onClick={hide} />
          <SideNavigation.Logo url={`/#${PORTAL_LINK_SHIPMENTS_PATH}`}>
            <NavigationLogo navPosition="side" />
          </SideNavigation.Logo>

          <SideNavigation.Menu>
            <SideNavigation.Menu.Item
              active={
                pathname === PORTAL_LINK_SHIPMENTS_PATH ||
                isActivePath(`${PORTAL_BASE_PATH}/shipment`, pathname)
              }
              label="Shipments"
              url={`/#${PORTAL_LINK_SHIPMENTS_PATH}`}
            />
            <SideNavigation.Menu.Item
              active={isActivePath(`${PORTAL_BASE_PATH}/bids`, pathname)}
              label="Bids & Tenders"
              url={`/#${PORTAL_BASE_PATH}/bids-list/`}
            />
            <SideNavigation.Menu.Item
              active={isActivePath(`${PORTAL_BASE_PATH}/rfp`, pathname)}
              label="RFPs"
              url={`/#${PORTAL_BASE_PATH}/rfp/`}
            />
            {enableShipperListPage.toBoolean() && (
              <SideNavigation.Menu label="manage">
                <SideNavigation.Menu.Item
                  active={isActivePath(`${PORTAL_BASE_PATH}/shippers`, pathname)}
                  label="Shippers"
                  url={`/#${PORTAL_BASE_PATH}/shippers/`}
                />
              </SideNavigation.Menu>
            )}
          </SideNavigation.Menu>
          {process.env.REACT_APP_RELEASE_VERSION && (
            <Layout.Box
              padding="none"
              paddingL="spacing-6"
              paddingB="spacing-4"
              style={{ marginTop: 'auto' }}
            >
              <Text as="p" variant="body-sm" color="color-text-tertiary">
                v{process.env.REACT_APP_RELEASE_VERSION}
              </Text>
            </Layout.Box>
          )}
        </SideNavigation>
      </SideNavCollapseWrapper>
      <StyledMainWrapper color={color} padding={padding}>
        <TopNavCollapseWrapper>
          <TopNavigation>
            <TopNavigation.OpenSideNavButton onClick={toggle} />
            <TopNavigation.Logo url={`/#${PORTAL_LINK_SHIPMENTS_PATH}`}>
              <NavigationLogo navPosition="top" />
            </TopNavigation.Logo>
            <TopNavigation.Menu align="right">
              <TopNavigation.Menu.Item label={user.email() ?? ''}>
                <TopNavigation.Menu.SubItem
                  label="Logout"
                  onClick={event => {
                    event.preventDefault()
                    user.logout()
                    window.location.href = '/login'
                  }}
                />
              </TopNavigation.Menu.Item>
            </TopNavigation.Menu>
          </TopNavigation>
        </TopNavCollapseWrapper>
        <main>{children}</main>
      </StyledMainWrapper>
    </StyledPageWIthSideNav>
  )
}

export default LoggedInNavigation
